import { Formik, Form, Field } from 'formik'
import React from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button, BUTTON_VARIANT } from 'components/Button'
import { Input } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { Toast } from 'components/Toast'
import { BORDER_WIDTH, COLOR, SPACE } from 'Theme'
import { useUpdateExpertUserMutation } from 'types/graphql-generated'

const validationSchema = Yup.object().shape({
  email: Yup.string().email().trim().label('Email address').required(),
  firstName: Yup.string().trim().label('First name').required(),
  lastName: Yup.string().trim().label('Last name').required(),
})

const StyledFlex = styled(Flex)`
  border-top: ${({ disabledBorder }) =>
    disabledBorder
      ? 'none'
      : `${BORDER_WIDTH.PX_1} solid ${COLOR.GRAYSCALE_5}`};
`

export const ExpertTeamMemberInvite = ({
  onCancel,
  refetch,
  customButtonLabel,
}: {
  onCancel?: () => void
  refetch?: () => void
  customButtonLabel?: string
}) => {
  const [inviteExpertUser, { loading: isInviteExpertUserLoading }] =
    useUpdateExpertUserMutation()

  return (
    <Formik
      initialValues={{
        email: '',
        firstName: '',
        lastName: '',
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur
      onSubmit={async ({ email, ...restValues }, { resetForm }) => {
        const response = await inviteExpertUser({
          variables: {
            input: { email: email.trim(), ...restValues },
          },
        })
        if (!response.errors) {
          Toast.success('Invitation has been sent to requested email')
          refetch?.()
          resetForm()
        }
      }}
    >
      <Form>
        <Box
          p={SPACE.PX_30}
          pb={0}
          width="100%"
          mb={customButtonLabel ? '4px' : SPACE.PX_20}
        >
          <Field name="firstName">
            {({ field, meta }) => (
              <Input
                label="First name"
                autocomplete={false}
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched && meta.error ? meta.error : null}
                hasHint
                {...field}
              />
            )}
          </Field>
          <Field name="lastName">
            {({ field, meta }) => (
              <Input
                label="Last name"
                autocomplete={false}
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched && meta.error ? meta.error : null}
                hasHint
                {...field}
              />
            )}
          </Field>
          <Field name="email">
            {({ field, meta }) => (
              <Input
                label="Email address"
                autocomplete={false}
                isError={meta.touched && meta.error !== undefined}
                hint={meta.touched && meta.error ? meta.error : null}
                hasHint
                {...field}
              />
            )}
          </Field>
        </Box>
        <StyledFlex
          p={SPACE.PX_20}
          pt={0}
          justifyContent="flex-end"
          disabledBorder={Boolean(customButtonLabel)}
        >
          {onCancel && (
            <Button
              width="initial"
              variant={BUTTON_VARIANT.OUTLINE}
              mt={SPACE.PX_20}
              mr={SPACE.PX_10}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            mt={customButtonLabel ? '2px' : SPACE.PX_20}
            mx={customButtonLabel ? '10px' : 0}
            isLoading={isInviteExpertUserLoading}
            width={customButtonLabel ? '100%' : 'initial'}
          >
            {customButtonLabel || 'Invite'}
          </Button>
        </StyledFlex>
      </Form>
    </Formik>
  )
}
